import { UnitOption } from './types';

/** A property and the unit numbers attached to the appointment at it. */
export interface UnitGroup {
  building: string;
  numbers: string[];
}

/**
 * Group an appointment's units by the property they belong to, preserving the
 * order the API returned them in.
 *
 * An appointment belongs to one property, so in practice this is one group —
 * grouping exists so a multi-building appointment can never render an
 * ambiguous "1002, 204".
 */
export const groupUnitsByBuilding = (
  units: UnitOption[] | undefined
): UnitGroup[] => {
  const groups: UnitGroup[] = [];
  (units ?? []).forEach((unit) => {
    const building = unit.building_name ?? '';
    const number = unit.name ?? unit.label ?? '';
    const existing = groups.find((group) => group.building === building);
    if (existing) {
      existing.numbers.push(number);
    } else {
      groups.push({ building, numbers: [number] });
    }
  });
  return groups;
};

export interface UnitsSummary {
  /** What the row shows, e.g. "1002, 1001" or "North Cove · 1002, 1001". */
  text: string;
  /** Units beyond the visible cap, e.g. 1 for "+1". 0 when nothing is hidden. */
  hidden: number;
  /** Every unit, one full label per line — for the hover tooltip. */
  tooltip: string;
}

const MAX_VISIBLE_UNITS = 2;

/**
 * Summarise an appointment's units for a list row.
 *
 * The property is named at most once, and only when the caller asks for it
 * (`showProperty: true`) — every real surface already names the property in its
 * own column, in the page header, or on the certification file. Repeating it
 * inside every unit label is what used to wrap the row onto three lines:
 * "NORTH COVE APARTMENTS — 1002, NORTH COVE APARTMENTS — 1001".
 */
export const unitsSummary = (
  units: UnitOption[] | undefined,
  { showProperty }: { showProperty: boolean }
): UnitsSummary | null => {
  const groups = groupUnitsByBuilding(units);
  const total = groups.reduce((count, group) => count + group.numbers.length, 0);
  if (total === 0) {
    return null;
  }

  const visible: string[] = [];
  let remaining = MAX_VISIBLE_UNITS;
  groups.forEach((group) => {
    if (remaining <= 0) {
      return;
    }
    const numbers = group.numbers.slice(0, remaining);
    remaining -= numbers.length;
    const joined = numbers.join(', ');
    // Name the property only when asked to, or when units span more than one
    // building (where numbers alone would be ambiguous).
    const needsBuilding = showProperty || groups.length > 1;
    visible.push(needsBuilding ? `${group.building} · ${joined}` : joined);
  });

  const hidden = total - Math.min(total, MAX_VISIBLE_UNITS);

  return {
    text: visible.join(' · '),
    hidden,
    tooltip: (units ?? [])
      .map((unit) => unit.label ?? `${unit.building_name} — ${unit.name}`)
      .join('\n'),
  };
};
